import {
  PENDING_REFERRAL_BOX_CODES_KEY,
  RewardToastSystem,
} from '@/services/reward-toast-service'
import { useUserStore } from '@/stores/user'
import type { User } from '@/types'
import { watch } from 'vue'

/**
 * Composable for managing authentication state watchers.
 * Handles reward subscription setup/cleanup based on user login/logout state changes.
 *
 * Note: This composable must be used after Pinia is initialized to avoid
 * "getActivePinia was called with no active Pinia" errors.
 */
export function useAuthWatchers() {
  /**
   * Sets up authentication state watchers for rewards subscriptions.
   * This should be called after Pinia is active to avoid "getActivePinia" errors.
   */
  const setupAuthWatchers = async () => {
    const userStore = useUserStore()

    watch<User | null, true>(
      () => userStore.user,
      async (newUser: User | null, oldUser: User | null | undefined) => {
        const isLoggingIn = !oldUser && newUser
        const isLoggingOut = oldUser && !newUser
        const isUserChanging = oldUser && newUser && oldUser.id !== newUser.id

        // Handle login or user change
        if ((isLoggingIn || isUserChanging) && newUser?.id) {
          // Clean up old subscriptions if user is changing
          if (isUserChanging && oldUser?.id) {
            await RewardToastSystem.cleanupRewardsSubscription(oldUser.id)
          }

          // Setup new subscription
          await RewardToastSystem.setupRewardsSubscription(newUser.id)

          // If any referral box codes were received while logged out, route now
          try {
            const existing = JSON.parse(
              localStorage.getItem(PENDING_REFERRAL_BOX_CODES_KEY) || '[]'
            ) as string[]
            if (Array.isArray(existing) && existing.length > 0) {
              const code = existing.shift() as string
              localStorage.setItem(
                PENDING_REFERRAL_BOX_CODES_KEY,
                JSON.stringify(existing)
              )
              RewardToastSystem.routeToReferralBox(code)
            }
          } catch (e) {
            console.error(
              'Failed to process pending referral boxes after login',
              e
            )
          }
        }

        // Handle logout
        if (isLoggingOut && oldUser?.id) {
          await RewardToastSystem.cleanupRewardsSubscription(oldUser.id)
        }
      },
      { immediate: true }
    )
  }

  return {
    setupAuthWatchers,
  }
}
